import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';

import { usePublicProfile } from '@/hooks/useProfile';
import { usePublicProjects } from '@/hooks/useProjects';
import { resolveImageUrl } from '@/utils/url';
import { getErrorMessage } from '@/utils/error';

export function PortfolioPage() {
  const { data: profile, isLoading: profileLoading, error: profileError } = usePublicProfile();
  const { data: projectsData, isLoading: projectsLoading } = usePublicProjects();

  const isLoading = profileLoading || projectsLoading;
  const projects = projectsData?.content ?? [];

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-background">
        <Loader2 className="h-8 w-8 animate-spin text-dark-primary" />
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-background text-dark-on-surface">
        <p className="text-error">{getErrorMessage(profileError)}</p>
      </div>
    );
  }

  const firstName = profile?.name?.split(' ')[0] ?? '';
  const lastName = profile?.name?.split(' ').slice(1).join(' ') ?? '';

  return (
    <div className="min-h-screen bg-dark-background font-sans text-dark-on-surface selection:bg-dark-secondary/30">
      {/* ── TopNavBar ── */}
      <nav className="fixed top-0 z-50 w-full bg-[#111319]/80 shadow-[0_20px_40px_-15px_rgba(0,26,64,0.4)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-8 py-4">
          <a href="https://duongvu.dev" target="_blank" rel="noopener noreferrer" className="font-headline text-xl font-black tracking-tighter text-white">
            duongvu.dev
          </a>
          <div className="hidden items-center gap-8 md:flex">
            <a
              href="#"
              className="relative font-headline font-bold tracking-tight text-dark-secondary-container after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:bg-dark-primary-container after:content-['']"
            >
              Home
            </a>
            <a
              href="#projects"
              className="rounded-md px-2 py-1 font-headline font-medium tracking-tight text-dark-on-surface transition-all duration-300 hover:bg-white/5 hover:text-dark-secondary"
            >
              Projects
            </a>
            <a
              href="#about"
              className="rounded-md px-2 py-1 font-headline font-medium tracking-tight text-dark-on-surface transition-all duration-300 hover:bg-white/5 hover:text-dark-secondary"
            >
              About
            </a>
            <a
              href="#contact"
              className="rounded-md px-2 py-1 font-headline font-medium tracking-tight text-dark-on-surface transition-all duration-300 hover:bg-white/5 hover:text-dark-secondary"
            >
              Contact
            </a>
          </div>
          <button className="rounded-full bg-gradient-to-br from-dark-primary-container to-dark-secondary-container px-6 py-2 font-headline text-sm font-bold text-white transition-transform active:scale-95">
            Download CV
          </button>
        </div>
      </nav>

      <main className="pt-24">
        {/* ── Hero Section ── */}
        <section className="relative flex min-h-[40vh] flex-col justify-center overflow-hidden px-8">
          <div className="pointer-events-none absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #424752 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-dark-outline-variant/15 bg-dark-surface-container-highest px-3 py-1">
                <span className="h-2 w-2 animate-pulse rounded-full bg-dark-secondary" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-dark-secondary">
                  Available for new challenges
                </span>
              </div>
              <h1 className="mb-6 font-headline text-4xl font-extrabold leading-tight tracking-tighter text-white md:text-5xl">
                {firstName}{' '}
                <span className="bg-gradient-to-r from-dark-primary via-dark-secondary to-dark-secondary-container bg-clip-text text-transparent">
                  {lastName}
                </span>
              </h1>
              <p
                className="mb-10 max-w-2xl font-headline text-xl font-light leading-relaxed text-dark-on-surface-variant md:text-2xl"
                dangerouslySetInnerHTML={{
                  __html: profile?.executiveSummary || 'Building architecturally sound digital experiences.',
                }}
              />
              <div className="flex flex-wrap gap-4">
                <a
                  href="#projects"
                  className="flex items-center gap-2 rounded-full bg-gradient-to-br from-dark-primary-container to-dark-secondary-container px-6 py-3 font-headline text-sm font-bold text-white shadow-[0_20px_40px_-15px_rgba(0,26,64,0.4)] transition-transform hover:scale-105"
                >
                  View Work <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#about"
                  className="rounded-full border border-dark-outline-variant px-6 py-3 font-headline text-sm font-bold transition-colors hover:bg-dark-surface-container"
                >
                  Technical Stack
                </a>
              </div>
            </div>
            <div className="relative lg:col-span-5">
              <div className="group relative z-20 aspect-square overflow-hidden rounded-xl shadow-[0_20px_40px_-15px_rgba(0,26,64,0.4)]">
                {profile?.avatarUrl ? (
                  <img
                    alt={profile.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    src={resolveImageUrl(profile.avatarUrl) || ''}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-dark-surface-container text-6xl font-black text-dark-outline">
                    {firstName.charAt(0)}
                    {lastName.charAt(0)}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-dark-background via-transparent to-transparent opacity-60" />
              </div>
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-dark-primary-container/20 blur-3xl" />
              <div className="absolute -bottom-10 -left-10 h-60 w-60 rounded-full bg-dark-secondary-container/10 blur-3xl" />
            </div>
          </div>
        </section>

        {/* ── About & Tech Stack ── */}
        <section className="relative overflow-hidden bg-dark-surface-container-low px-8 py-16" id="about">
          <div className="mx-auto grid max-w-7xl items-start gap-24 lg:grid-cols-2">
            <div>
              <span className="mb-4 block text-[10px] font-bold uppercase tracking-[0.2em] text-dark-secondary">
                Engineered Legacy
              </span>
              <h2 className="mb-8 font-headline text-2xl font-extrabold tracking-tight text-white">
                The Architectural Approach
              </h2>
              <div className="space-y-6 text-lg leading-relaxed text-dark-on-surface-variant">
                {profile?.biography ? (
                  profile.biography.split('\n').map((p, i) => <p key={i}>{p}</p>)
                ) : (
                  <p>Building systems with a foundation in performance and scalability.</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {(profile?.techCategories ?? []).map((cat, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container p-8 transition-colors hover:border-dark-primary/30"
                >
                  <h3 className="mb-4 font-headline font-bold text-white">{cat.title}</h3>
                  <div className="flex flex-wrap gap-2">
                    {cat.tags.map((tag, j) => (
                      <span
                        key={j}
                        className="rounded-md bg-dark-surface-container-highest px-3 py-1 text-[11px] font-bold text-dark-secondary"
                      >
                        {tag.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── All Projects Grid ── */}
        <section className="bg-dark-surface-container-low/50 px-8 py-16" id="projects">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12">
              <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-dark-primary-container">
                Archive
              </span>
              <h2 className="font-headline text-3xl font-extrabold tracking-tight text-white">
                All Projects
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {projects.map((project) => (
                <Link
                  key={project.id}
                  to={`project/${project.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container transition-all hover:border-dark-primary-container/30"
                >
                  <div className="flex justify-center pt-5">
                    <div className="aspect-square w-4/5 overflow-hidden rounded-xl">
                      {project.thumbnailUrl ? (
                        <img
                          alt={project.name}
                          className="h-full w-full object-cover transition-all duration-500"
                          src={resolveImageUrl(project.thumbnailUrl) || ''}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-dark-surface-container-highest text-2xl font-black text-dark-outline">
                          {project.name.charAt(0)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h4 className="font-headline text-lg font-bold text-white transition-colors group-hover:text-dark-primary">
                      {project.name}
                    </h4>
                    <p className="mb-2 mt-1 text-xs text-dark-on-surface-variant">
                      
                    </p>
                    {project.shortDescription && (
                      <p className="mb-3 line-clamp-3 text-xs leading-relaxed text-dark-on-surface-variant/80">
                        {project.shortDescription}
                      </p>
                    )}
                    {project.tags && project.tags.length > 0 && (
                      <div className="mt-auto flex flex-wrap gap-1.5">
                        {project.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="rounded-md bg-dark-surface-container-highest px-2 py-0.5 text-[10px] font-bold text-dark-secondary"
                          >
                            {tag.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
              {projects.length === 0 && (
                <p className="col-span-full py-16 text-center text-dark-on-surface-variant">
                  No projects available yet.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── CTA Section ── */}
        <section className="px-8 py-10" id="contact">
          <div className="mx-auto max-w-7xl">
            <div className="relative flex flex-col items-center overflow-hidden rounded-3xl bg-gradient-to-br from-dark-primary-container to-dark-secondary-container p-12 text-center md:p-24">
              <div className="pointer-events-none absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle, #424752 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
              <div className="relative z-10">
                <h2 className="mb-8 font-headline text-4xl font-extrabold tracking-tighter text-white md:text-6xl">
                  Ready to start the next build?
                </h2>
                <p className="mx-auto mb-12 max-w-xl text-xl text-white/80">
                  Available for select projects and engineering partnerships. Let&apos;s discuss
                  your architectural needs.
                </p>
                <a
                  href="mailto:duongtuanvu1111@gmail.com"
                  className="inline-flex items-center gap-3 rounded-full bg-white px-10 py-5 font-headline text-lg font-bold text-dark-primary-container shadow-[0_20px_40px_-15px_rgba(0,26,64,0.4)] transition-transform hover:scale-105"
                >
                  Initiate Contact <ArrowRight className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="w-full border-t border-dark-outline-variant/15 bg-[#0C0E14] px-8 py-12">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 md:flex-row">
          <a href="https://duongvu.dev" target="_blank" rel="noopener noreferrer" className="text-lg font-bold text-white">duongvu.dev</a>
          <p className="text-center text-sm uppercase tracking-widest text-dark-on-surface">
            &copy; {new Date().getFullYear()} Portfolio. Built with architectural integrity.
          </p>
        </div>
      </footer>
    </div>
  );
}
